// src/components/ui/IconPicker/IconPicker.tsx
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import * as IonicIcons from 'ionicons/icons';
import { cloudUploadOutline, linkOutline, imageOutline, closeCircle } from 'ionicons/icons';
import Icon from '@mdi/react';
import * as MdiIcons from '@mdi/js';
import * as LucideIcons from 'lucide-react';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { init, SearchIndex } from 'emoji-mart';
import { Segment, type SegmentOption } from '../Segment';
import { TextInput } from '../TextInput';

// Initialize emoji-mart data for search functionality
init({ data });

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export type IconType = 'ionic' | 'mdi' | 'lucide' | 'emoji' | 'upload' | 'url';

export interface IconValue {
  type: IconType;
  name: string;
  /** For upload/url types, this holds the image URL or data URI */
  imageUrl?: string;
}

export interface IconPickerProps {
  /** Currently selected icon */
  value?: IconValue | null;
  /** Change handler */
  onChange: (icon: IconValue) => void;
  /** Default tab to show */
  defaultTab?: IconType;
  /** Limit which tabs are shown */
  tabs?: IconType[];
  /** Size of icons in the grid */
  iconSize?: number;
  /** Maximum height of the picker */
  maxHeight?: number;
  /** Custom className */
  className?: string;
  /** Maximum file size in bytes for uploads (default: 2MB) */
  maxFileSize?: number;
  /** Accepted file types for upload (default: image/*) */
  acceptedFileTypes?: string;
  /** Callback when file upload occurs - can be used for server upload */
  onFileUpload?: (file: File) => Promise<string>;
}

// ═══════════════════════════════════════════════════════════════════
// ICON DATA - Using actual export names from libraries
// ═══════════════════════════════════════════════════════════════════

// Helper to convert kebab-case to camelCase (e.g., "home-outline" -> "homeOutline")
function kebabToCamel(str: string): string {
  return str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Common Ionicons - these are the EXPORT NAMES from ionicons/icons (camelCase)
const IONIC_ICONS = [
  'homeOutline', 'personOutline', 'peopleOutline', 'settingsOutline',
  'calendarOutline', 'timeOutline', 'alarmOutline', 'notificationsOutline',
  'mailOutline', 'chatbubbleOutline', 'callOutline', 'videocamOutline',
  'cameraOutline', 'imageOutline', 'imagesOutline', 'folderOutline',
  'documentOutline', 'documentsOutline', 'clipboardOutline', 'createOutline',
  'trashOutline', 'archiveOutline', 'downloadOutline', 'cloudOutline',
  'heartOutline', 'starOutline', 'bookmarkOutline', 'flagOutline',
  'checkmarkCircleOutline', 'closeCircleOutline', 'addCircleOutline', 'removeCircleOutline',
  'cartOutline', 'bagOutline', 'walletOutline', 'cardOutline',
  'giftOutline', 'pricetagOutline', 'receiptOutline', 'cashOutline',
  'restaurantOutline', 'cafeOutline', 'pizzaOutline', 'beerOutline',
  'wineOutline', 'nutritionOutline', 'iceCreamOutline', 'fastFoodOutline',
  'bedOutline', 'medicalOutline', 'fitnessOutline', 'bicycleOutline',
  'carOutline', 'busOutline', 'trainOutline', 'airplaneOutline',
  'boatOutline', 'walkOutline', 'footstepsOutline', 'navigateOutline',
  'mapOutline', 'locationOutline', 'compassOutline', 'earthOutline',
  'sunnyOutline', 'moonOutline', 'cloudyOutline', 'rainyOutline',
  'thunderstormOutline', 'snowOutline', 'waterOutline', 'flameOutline',
  'leafOutline', 'flowerOutline', 'roseOutline', 'pawOutline',
  'fishOutline', 'bugOutline', 'bulbOutline', 'flashlightOutline',
  'musicalNotesOutline', 'headsetOutline', 'micOutline', 'volumeHighOutline',
  'tvOutline', 'desktopOutline', 'laptopOutline', 'phonePortraitOutline',
  'tabletPortraitOutline', 'watchOutline', 'gameControllerOutline', 'diceOutline',
  'footballOutline', 'basketballOutline', 'baseballOutline', 'tennisballOutline',
  'trophyOutline', 'medalOutline', 'ribbonOutline', 'schoolOutline',
  'libraryOutline', 'bookOutline', 'readerOutline', 'glassesOutline',
  'brushOutline', 'colorPaletteOutline', 'constructOutline', 'hammerOutline',
  'buildOutline', 'cogOutline', 'keyOutline', 'lockClosedOutline',
  'lockOpenOutline', 'shieldOutline', 'fingerPrintOutline', 'eyeOutline',
  'happyOutline', 'sadOutline', 'skullOutline', 'sparklesOutline',
  'rocketOutline', 'planetOutline', 'globeOutline', 'telescopeOutline',
];

// Material Design Icons - using actual export names from @mdi/js
const MDI_ICONS = [
  'mdiHome', 'mdiAccount', 'mdiAccountGroup', 'mdiCog',
  'mdiCalendar', 'mdiClock', 'mdiAlarm', 'mdiBell',
  'mdiEmail', 'mdiMessage', 'mdiPhone', 'mdiVideo',
  'mdiCamera', 'mdiImage', 'mdiImageMultiple', 'mdiFolder',
  'mdiFile', 'mdiFileMultiple', 'mdiClipboard', 'mdiPencil',
  'mdiDelete', 'mdiArchive', 'mdiDownload', 'mdiCloud',
  'mdiHeart', 'mdiStar', 'mdiBookmark', 'mdiFlag',
  'mdiCheckCircle', 'mdiCloseCircle', 'mdiPlusCircle', 'mdiMinusCircle',
  'mdiCart', 'mdiShopping', 'mdiWallet', 'mdiCreditCard',
  'mdiGift', 'mdiTag', 'mdiReceipt', 'mdiCash',
  'mdiSilverwareForkKnife', 'mdiCoffee', 'mdiPizza', 'mdiGlassMugVariant',
  'mdiGlassWine', 'mdiFood', 'mdiIceCream', 'mdiHamburger',
  'mdiBed', 'mdiMedicalBag', 'mdiDumbbell', 'mdiBike',
  'mdiCar', 'mdiBus', 'mdiTrain', 'mdiAirplane',
  'mdiFerry', 'mdiWalk', 'mdiRun', 'mdiNavigation',
  'mdiMap', 'mdiMapMarker', 'mdiCompass', 'mdiEarth',
  'mdiWeatherSunny', 'mdiWeatherNight', 'mdiWeatherCloudy', 'mdiWeatherRainy',
  'mdiWeatherLightning', 'mdiWeatherSnowy', 'mdiWater', 'mdiFire',
  'mdiLeaf', 'mdiFlower', 'mdiRose', 'mdiPaw',
  'mdiFish', 'mdiBug', 'mdiLightbulb', 'mdiFlashlight',
  'mdiMusic', 'mdiHeadphones', 'mdiMicrophone', 'mdiVolumeHigh',
  'mdiTelevision', 'mdiMonitor', 'mdiLaptop', 'mdiCellphone',
  'mdiTablet', 'mdiWatch', 'mdiControllerClassic', 'mdiDice5',
  'mdiFootball', 'mdiBasketball', 'mdiBaseball', 'mdiTennis',
  'mdiTrophy', 'mdiMedal', 'mdiRibbon', 'mdiSchool',
  'mdiLibrary', 'mdiBook', 'mdiBookOpen', 'mdiGlasses',
  'mdiBrush', 'mdiPalette', 'mdiWrench', 'mdiHammer',
  'mdiTools', 'mdiCogs', 'mdiKey', 'mdiLock',
  'mdiLockOpen', 'mdiShield', 'mdiFingerprint', 'mdiEye',
  'mdiEmoticonHappy', 'mdiEmoticonSad', 'mdiSkull', 'mdiAutoFix',
  'mdiRocket', 'mdiWeb', 'mdiTelescope',
];

// Lucide icons (from lucide-react)
const LUCIDE_ICONS = [
  'Home', 'User', 'Users', 'Settings',
  'Calendar', 'Clock', 'AlarmClock', 'Bell',
  'Mail', 'MessageCircle', 'Phone', 'Video',
  'Camera', 'Image', 'Images', 'Folder',
  'File', 'Files', 'Clipboard', 'Pencil',
  'Trash2', 'Archive', 'Download', 'Cloud',
  'Heart', 'Star', 'Bookmark', 'Flag',
  'CheckCircle', 'XCircle', 'PlusCircle', 'MinusCircle',
  'ShoppingCart', 'ShoppingBag', 'Wallet', 'CreditCard',
  'Gift', 'Tag', 'Receipt', 'DollarSign',
  'UtensilsCrossed', 'Coffee', 'Pizza', 'Beer',
  'Wine', 'Apple', 'IceCream', 'Sandwich',
  'Bed', 'Stethoscope', 'Dumbbell', 'Bike',
  'Car', 'Bus', 'Train', 'Plane',
  'Ship', 'Footprints', 'Activity', 'Navigation',
  'Map', 'MapPin', 'Compass', 'Globe',
  'Sun', 'Moon', 'Cloud', 'CloudRain',
  'CloudLightning', 'Snowflake', 'Droplet', 'Flame',
  'Leaf', 'Flower', 'Flower2', 'PawPrint',
  'Fish', 'Bug', 'Lightbulb', 'Flashlight',
  'Music', 'Headphones', 'Mic', 'Volume2',
  'Tv', 'Monitor', 'Laptop', 'Smartphone',
  'Tablet', 'Watch', 'Gamepad2', 'Dice5',
  'Trophy', 'Medal', 'Award', 'GraduationCap',
  'Library', 'Book', 'BookOpen', 'Glasses',
  'Brush', 'Palette', 'Wrench', 'Hammer',
  'Hammer', 'Cog', 'Key', 'Lock',
  'Unlock', 'Shield', 'Fingerprint', 'Eye',
  'Smile', 'Frown', 'Skull', 'Sparkles',
  'Rocket', 'Globe2', 'Telescope',
];

// Note: Emoji data is now provided by @emoji-mart/data with full keyword search
// The emoji-mart Picker component handles all emoji rendering, categories, and search
// This replaces the previous curated EMOJI_DATA array with the full Unicode emoji set

// ═══════════════════════════════════════════════════════════════════
// ICON PICKER COMPONENT
// ═══════════════════════════════════════════════════════════════════

const TAB_OPTIONS: SegmentOption<IconType>[] = [
  { value: 'emoji', label: 'Emoji' },
  { value: 'ionic', label: 'Ionic' },
  { value: 'mdi', label: 'Material' },
  { value: 'lucide', label: 'Lucide' },
  { value: 'upload', label: 'Upload' },
  { value: 'url', label: 'URL' },
];

export const IconPicker: React.FC<IconPickerProps> = ({
  value,
  onChange,
  defaultTab = 'emoji',
  tabs = ['emoji', 'ionic', 'mdi', 'lucide', 'upload', 'url'],
  iconSize = 24,
  maxHeight = 300,
  className,
  maxFileSize = 2 * 1024 * 1024, // 2MB default
  acceptedFileTypes = 'image/*',
  onFileUpload,
}) => {
  const [activeTab, setActiveTab] = useState<IconType>(defaultTab);
  const [search, setSearch] = useState('');
  const [emojiSearch, setEmojiSearch] = useState('');
  const [searchedEmojis, setSearchedEmojis] = useState<Array<{ id: string; native: string; name: string }>>([]);
  const [urlInput, setUrlInput] = useState('');
  const [urlPreview, setUrlPreview] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Search emojis when search term changes
  useEffect(() => {
    const searchEmojis = async () => {
      if (!emojiSearch.trim()) {
        setSearchedEmojis([]);
        return;
      }
      try {
        const results = await SearchIndex.search(emojiSearch);
        const emojis = results?.map((emoji: { id: string; skins: Array<{ native: string }>; name: string }) => ({
          id: emoji.id,
          native: emoji.skins[0]?.native || '',
          name: emoji.name,
        })).filter((e: { native: string }) => e.native) || [];
        setSearchedEmojis(emojis);
      } catch (err) {
        console.error('Emoji search error:', err);
        setSearchedEmojis([]);
      }
    };
    searchEmojis();
  }, [emojiSearch]);

  // Initialize URL/upload preview from current value
  React.useEffect(() => {
    if (value?.type === 'url' && value.imageUrl) {
      setUrlInput(value.imageUrl);
      setUrlPreview(value.imageUrl);
    } else if (value?.type === 'upload' && value.imageUrl) {
      setUploadPreview(value.imageUrl);
    }
  }, [value]);

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File) => {
    setUploadError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      setUploadError(`File too large. Max size: ${Math.round(maxFileSize / 1024 / 1024)}MB`);
      return;
    }

    setIsUploading(true);

    try {
      let imageUrl: string;

      if (onFileUpload) {
        // Use custom upload handler (e.g., to Supabase storage)
        imageUrl = await onFileUpload(file);
      } else {
        // Convert to data URI for local preview
        imageUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      setUploadPreview(imageUrl);
      onChange({ type: 'upload', name: file.name, imageUrl });
    } catch (err) {
      setUploadError('Failed to upload image');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  }, [maxFileSize, onFileUpload, onChange]);

  // Handle file input change
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Handle URL input
  const handleUrlSubmit = useCallback(() => {
    setUrlError(null);
    const trimmedUrl = urlInput.trim();

    if (!trimmedUrl) {
      setUrlError('Please enter a URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(trimmedUrl);
    } catch {
      setUrlError('Please enter a valid URL');
      return;
    }

    setUrlPreview(trimmedUrl);
    onChange({ type: 'url', name: 'custom-url', imageUrl: trimmedUrl });
  }, [urlInput, onChange]);

  // Handle URL preview load error
  const handleUrlImageError = useCallback(() => {
    setUrlError('Failed to load image from URL');
    setUrlPreview(null);
  }, []);

  // Clear upload
  const clearUpload = useCallback(() => {
    setUploadPreview(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Clear URL - also deselects the icon if it was a URL type
  const clearUrl = useCallback(() => {
    setUrlInput('');
    setUrlPreview(null);
    setUrlError(null);
    // If current selection is a URL type, clear it
    if (value?.type === 'url') {
      onChange({ type: 'emoji', name: '📋' }); // Reset to default emoji
    }
  }, [value, onChange]);

  // Filter tab options based on props
  const filteredTabs = useMemo(() =>
    TAB_OPTIONS.filter(t => tabs.includes(t.value)),
    [tabs]
  );

  // Get filtered icons based on search and active tab
  const filteredIcons = useMemo(() => {
    const query = search.toLowerCase().trim();

    switch (activeTab) {
      case 'ionic':
        return query
          ? IONIC_ICONS.filter(icon => icon.toLowerCase().includes(query))
          : IONIC_ICONS;
      case 'mdi':
        return query
          ? MDI_ICONS.filter(icon => icon.toLowerCase().replace('mdi', '').includes(query))
          : MDI_ICONS;
      case 'lucide':
        return query
          ? LUCIDE_ICONS.filter(icon => icon.toLowerCase().includes(query))
          : LUCIDE_ICONS;
      // Note: emoji tab now uses emoji-mart picker directly, not this filtered list
      default:
        return [];
    }
  }, [activeTab, search]);

  // Handle icon selection
  const handleSelect = useCallback((name: string) => {
    onChange({ type: activeTab, name });
  }, [activeTab, onChange]);

  // Check if icon is selected
  const isSelected = useCallback((name: string) => {
    return value?.type === activeTab && value?.name === name;
  }, [value, activeTab]);

  // Render icon based on type
  const renderIcon = (iconName: string, size: number) => {
    switch (activeTab) {
      case 'ionic': {
        // Get the icon from ionicons/icons using the camelCase name
        const ionicIcon = (IonicIcons as Record<string, string>)[iconName];
        if (ionicIcon) {
          return <IonIcon icon={ionicIcon} style={{ fontSize: size }} />;
        }
        return <span style={{ fontSize: size * 0.5, color: 'var(--ion-color-medium)' }}>?</span>;
      }
      case 'mdi': {
        // Get the path from @mdi/js
        const mdiPath = (MdiIcons as Record<string, string>)[iconName];
        if (mdiPath) {
          return <Icon path={mdiPath} size={size / 24} />;
        }
        return <span style={{ fontSize: size * 0.5, color: 'var(--ion-color-medium)' }}>?</span>;
      }
      case 'lucide': {
        const LucideIcon = (LucideIcons as unknown as Record<string, React.ComponentType<{ size?: number }>>)[iconName];
        if (LucideIcon) {
          return <LucideIcon size={size} />;
        }
        return <span style={{ fontSize: size * 0.5, color: 'var(--ion-color-medium)' }}>?</span>;
      }
      case 'emoji':
        return <span style={{ fontSize: size }}>{iconName}</span>;
      default:
        return null;
    }
  };

  // Get display name for tooltip
  const getDisplayName = (iconName: string): string => {
    switch (activeTab) {
      case 'ionic':
        // Convert camelCase to readable: "homeOutline" -> "Home Outline"
        return iconName
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
      case 'mdi':
        // Remove "mdi" prefix and convert: "mdiHome" -> "Home"
        return iconName
          .replace(/^mdi/, '')
          .replace(/([A-Z])/g, ' $1')
          .trim();
      case 'lucide':
        // Convert PascalCase to readable: "ShoppingCart" -> "Shopping Cart"
        return iconName
          .replace(/([A-Z])/g, ' $1')
          .trim();
      default:
        return iconName;
    }
  };

  // Render upload tab content
  const renderUploadTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 32,
          background: isDragging
            ? 'var(--ion-color-primary-tint)'
            : 'var(--ion-color-step-50)',
          border: `2px dashed ${isDragging ? 'var(--ion-color-primary)' : 'var(--ion-color-step-200)'}`,
          borderRadius: 12,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          minHeight: 150,
        }}
      >
        {isUploading ? (
          <>
            <div style={{
              width: 40,
              height: 40,
              border: '3px solid var(--ion-color-step-200)',
              borderTopColor: 'var(--ion-color-primary)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <span style={{
              fontSize: 14,
              color: 'var(--ion-color-medium)',
              fontFamily: 'var(--kff-data-font)',
            }}>
              Uploading...
            </span>
          </>
        ) : uploadPreview ? (
          <div style={{ position: 'relative' }}>
            <img
              src={uploadPreview}
              alt="Uploaded"
              style={{
                maxWidth: 100,
                maxHeight: 100,
                objectFit: 'contain',
                borderRadius: 8,
              }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearUpload();
              }}
              style={{
                position: 'absolute',
                top: -8,
                right: -8,
                width: 24,
                height: 24,
                padding: 0,
                background: 'var(--ion-color-danger)',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IonIcon icon={closeCircle} style={{ fontSize: 20, color: 'white' }} />
            </button>
          </div>
        ) : (
          <>
            <IonIcon
              icon={cloudUploadOutline}
              style={{
                fontSize: 48,
                color: isDragging ? 'var(--ion-color-primary)' : 'var(--ion-color-medium)',
              }}
            />
            <span style={{
              fontSize: 14,
              color: 'var(--ion-color-medium)',
              fontFamily: 'var(--kff-data-font)',
              textAlign: 'center',
            }}>
              Drag & drop an image here<br />
              or click to browse
            </span>
            <span style={{
              fontSize: 12,
              color: 'var(--ion-color-step-400)',
              fontFamily: 'var(--kff-data-font)',
            }}>
              Max size: {Math.round(maxFileSize / 1024 / 1024)}MB
            </span>
          </>
        )}
      </div>

      {/* Error message */}
      {uploadError && (
        <div style={{
          padding: '8px 12px',
          background: 'var(--ion-color-danger-tint)',
          color: 'var(--ion-color-danger-shade)',
          borderRadius: 8,
          fontSize: 13,
          fontFamily: 'var(--kff-data-font)',
        }}>
          {uploadError}
        </div>
      )}
    </div>
  );

  // Render URL tab content
  const renderUrlTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* URL input */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <TextInput
            value={urlInput}
            onChange={setUrlInput}
            placeholder="Enter image URL..."
            variant="default"
          />
        </div>
        <button
          type="button"
          onClick={handleUrlSubmit}
          style={{
            padding: '8px 16px',
            background: 'var(--ion-color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontFamily: 'var(--kff-data-font)',
            fontSize: 14,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <IonIcon icon={linkOutline} style={{ fontSize: 18 }} />
          Load
        </button>
      </div>

      {/* URL Preview */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 32,
          background: 'var(--ion-color-step-50)',
          border: '2px dashed var(--ion-color-step-200)',
          borderRadius: 12,
          minHeight: 150,
        }}
      >
        {urlPreview ? (
          <div style={{ position: 'relative' }}>
            <img
              src={urlPreview}
              alt="URL preview"
              onError={handleUrlImageError}
              style={{
                maxWidth: 100,
                maxHeight: 100,
                objectFit: 'contain',
                borderRadius: 8,
              }}
            />
            <button
              type="button"
              onClick={clearUrl}
              style={{
                position: 'absolute',
                top: -8,
                right: -8,
                width: 24,
                height: 24,
                padding: 0,
                background: 'var(--ion-color-danger)',
                border: 'none',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IonIcon icon={closeCircle} style={{ fontSize: 20, color: 'white' }} />
            </button>
          </div>
        ) : (
          <>
            <IonIcon
              icon={imageOutline}
              style={{ fontSize: 48, color: 'var(--ion-color-medium)' }}
            />
            <span style={{
              fontSize: 14,
              color: 'var(--ion-color-medium)',
              fontFamily: 'var(--kff-data-font)',
              textAlign: 'center',
            }}>
              Enter a URL above to preview
            </span>
          </>
        )}
      </div>

      {/* Error message */}
      {urlError && (
        <div style={{
          padding: '8px 12px',
          background: 'var(--ion-color-danger-tint)',
          color: 'var(--ion-color-danger-shade)',
          borderRadius: 8,
          fontSize: 13,
          fontFamily: 'var(--kff-data-font)',
        }}>
          {urlError}
        </div>
      )}
    </div>
  );

  // Handle emoji selection from emoji-mart
  const handleEmojiSelect = useCallback((emoji: { native?: string; id?: string }) => {
    if (emoji.native) {
      onChange({ type: 'emoji', name: emoji.native });
    }
  }, [onChange]);

  // Check if emoji is selected
  const isEmojiSelected = useCallback((native: string) => {
    return value?.type === 'emoji' && value?.name === native;
  }, [value]);

  // Render emoji picker with consistent styling
  const renderEmojiPicker = () => (
    <>
      {/* Search input - matches other tabs */}
      <TextInput
        value={emojiSearch}
        onChange={setEmojiSearch}
        placeholder="Search emojis..."
        variant="default"
      />

      {/* Show search results if searching, otherwise show category picker */}
      {emojiSearch.trim() ? (
        // Search results grid - matches icon grid styling
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${iconSize + 16}px, 1fr))`,
            gap: 4,
            maxHeight,
            overflowY: 'auto',
            padding: 4,
            background: 'var(--ion-color-step-50)',
            borderRadius: 8,
          }}
        >
          {searchedEmojis.map((emoji) => (
            <button
              key={emoji.id}
              type="button"
              onClick={() => onChange({ type: 'emoji', name: emoji.native })}
              title={emoji.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: iconSize + 16,
                height: iconSize + 16,
                padding: 0,
                background: isEmojiSelected(emoji.native)
                  ? 'var(--kff-forge-gold)'
                  : 'transparent',
                border: isEmojiSelected(emoji.native)
                  ? '2px solid var(--kff-forge-gold)'
                  : '2px solid transparent',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: iconSize,
                transition: 'all 0.15s ease',
              }}
            >
              {emoji.native}
            </button>
          ))}

          {searchedEmojis.length === 0 && (
            <div style={{
              gridColumn: '1 / -1',
              padding: 24,
              textAlign: 'center',
              color: 'var(--ion-color-medium)',
              fontFamily: 'var(--kff-data-font)',
              fontSize: 13,
            }}>
              No emojis found
            </div>
          )}
        </div>
      ) : (
        // Full emoji picker with matching background
        <div
          style={{
            maxHeight: maxHeight,
            overflow: 'hidden',
            borderRadius: 8,
            background: 'var(--ion-color-step-50)',
          }}
          className="emoji-mart-wrapper"
        >
          <style>{`
            .emoji-mart-wrapper em-emoji-picker {
              --rgb-background: transparent;
              --background: transparent;
              --border-color: transparent;
              --category-icon-size: 18px;
              --font-family: var(--kff-data-font);
              border: none;
              width: 100%;
            }
            .emoji-mart-wrapper em-emoji-picker .search {
              display: none;
            }
          `}</style>
          <Picker
            data={data}
            onEmojiSelect={handleEmojiSelect}
            theme="light"
            set="native"
            previewPosition="none"
            skinTonePosition="none"
            maxFrequentRows={2}
            perLine={8}
            emojiSize={iconSize}
            emojiButtonSize={iconSize + 12}
            navPosition="top"
            searchPosition="none"
            dynamicWidth={true}
          />
        </div>
      )}
    </>
  );

  // Render icon grid for standard tabs (ionic, mdi, lucide)
  const renderIconGrid = () => (
    <>
      {/* Search input */}
      <TextInput
        value={search}
        onChange={setSearch}
        placeholder="Search icons..."
        variant="default"
      />

      {/* Icon grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${iconSize + 16}px, 1fr))`,
          gap: 4,
          maxHeight,
          overflowY: 'auto',
          padding: 4,
          background: 'var(--ion-color-step-50)',
          borderRadius: 8,
        }}
      >
        {filteredIcons.map((iconName, index) => (
          <button
            key={`${iconName}-${index}`}
            type="button"
            onClick={() => handleSelect(iconName)}
            title={activeTab === 'emoji' ? undefined : getDisplayName(iconName)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: iconSize + 16,
              height: iconSize + 16,
              padding: 0,
              background: isSelected(iconName)
                ? 'var(--kff-forge-gold)'
                : 'transparent',
              border: isSelected(iconName)
                ? '2px solid var(--kff-forge-gold)'
                : '2px solid transparent',
              borderRadius: 8,
              cursor: 'pointer',
              color: isSelected(iconName)
                ? '#0a1a2a'
                : 'var(--ion-text-color)',
              transition: 'all 0.15s ease',
            }}
          >
            {renderIcon(iconName, iconSize)}
          </button>
        ))}

        {filteredIcons.length === 0 && (
          <div style={{
            gridColumn: '1 / -1',
            padding: 24,
            textAlign: 'center',
            color: 'var(--ion-color-medium)',
            fontFamily: 'var(--kff-data-font)',
            fontSize: 13,
          }}>
            No {activeTab === 'emoji' ? 'emoji' : 'icons'} found
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Tab selector */}
      {filteredTabs.length > 1 && (
        <Segment
          options={filteredTabs}
          value={activeTab}
          onChange={setActiveTab}
          size="sm"
          variant="minimal"
          fullWidth
        />
      )}

      {/* Tab content */}
      {activeTab === 'upload' && renderUploadTab()}
      {activeTab === 'url' && renderUrlTab()}
      {activeTab === 'emoji' && renderEmojiPicker()}
      {activeTab !== 'upload' && activeTab !== 'url' && activeTab !== 'emoji' && renderIconGrid()}
    </div>
  );
};

IconPicker.displayName = 'IconPicker';

// ═══════════════════════════════════════════════════════════════════
// HELPER COMPONENT: Render any IconValue
// ═══════════════════════════════════════════════════════════════════

export interface IconDisplayProps {
  icon: IconValue;
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const IconDisplay: React.FC<IconDisplayProps> = ({
  icon,
  size = 24,
  className,
  style,
}) => {
  const renderIcon = () => {
    switch (icon.type) {
      case 'ionic': {
        const ionicIcon = (IonicIcons as Record<string, string>)[icon.name];
        if (ionicIcon) {
          return <IonIcon icon={ionicIcon} style={{ fontSize: size, ...style }} className={className} />;
        }
        return <span style={{ fontSize: size, ...style }} className={className}>?</span>;
      }
      case 'mdi': {
        const mdiPath = (MdiIcons as Record<string, string>)[icon.name];
        if (mdiPath) {
          return <Icon path={mdiPath} size={size / 24} className={className} style={style} />;
        }
        return <span style={{ fontSize: size, ...style }} className={className}>?</span>;
      }
      case 'lucide': {
        const LucideIcon = (LucideIcons as unknown as Record<string, React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>>)[icon.name];
        if (LucideIcon) {
          return <LucideIcon size={size} className={className} style={style} />;
        }
        return <span style={{ fontSize: size, ...style }} className={className}>?</span>;
      }
      case 'emoji':
        return <span style={{ fontSize: size, ...style }} className={className}>{icon.name}</span>;
      case 'upload':
      case 'url':
        if (icon.imageUrl) {
          return (
            <img
              src={icon.imageUrl}
              alt={icon.name || 'Custom icon'}
              className={className}
              style={{
                width: size,
                height: size,
                objectFit: 'contain',
                borderRadius: 4,
                ...style,
              }}
            />
          );
        }
        return <IonIcon icon={imageOutline} style={{ fontSize: size, ...style }} className={className} />;
      default:
        return null;
    }
  };

  return renderIcon();
};

IconDisplay.displayName = 'IconDisplay';

export default IconPicker;
